import { useInsertionEffect } from 'react';

const injected = new Set<string>();

/*
 * Pseudo-class and media-query rules (hover, focus-visible, reduced motion) cannot be inline styles. Each component
 * registers one small rule block, keyed by id and scoped to a data-rv-* attribute, inserted once per document.
 *
 * `first`: insert the block before every other style in <head>, so a product stylesheet wins specificity ties. A
 * product look variant (for example the recon variants) uses it: the product's own layout classes beat its old look
 * rules by source order, and they keep beating the variant that replaces those rules.
 */
export function useStyleOnce(id: string, css: string, opts?: { first?: boolean }): void {
  const first = opts?.first ?? false;
  useInsertionEffect(() => {
    if (typeof document === 'undefined' || injected.has(id)) return;
    if (document.head.querySelector(`style[data-rv-style="${id}"]`)) {
      injected.add(id);
      return;
    }
    const el = document.createElement('style');
    el.setAttribute('data-rv-style', id);
    el.textContent = css;
    if (first) document.head.insertBefore(el, document.head.firstChild);
    else document.head.appendChild(el);
    injected.add(id);
  }, [id, css, first]);
}

export const FOCUS_RING = 'outline: 2px solid var(--rv-accent); outline-offset: 2px;';
export const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';
